import AttachFileIcon from '@mui/icons-material/AttachFile';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  downloadRequirementFile,
  getRequirementFileAsBlob,
  getRequirements,
  registerRequirementRecord,
} from 'lib/api/requirements';
import { Requirement, RequirementRecord } from 'lib/api/requirements/types';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DocumentsPreviewer from '../../common/DocumentsPreviewer';
import CustomButton from '../Button';
import CenteredCircularProgress from '../CircularProgress';

interface RequirementsDialogProps {
  open: boolean;
  onClose: () => void;
  category: string;
  entityKey: string;
  entityId: number | string;
  entityAlias: string;
}

const RequirementsDialog = (props: RequirementsDialogProps) => {
  const { open, onClose, category, entityKey, entityId, entityAlias } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedValues, setEditedValues] = useState<{
    [key: number]: {
      completed: boolean;
      observation: string;
      fileDeleted: boolean;
      file?: File;
    };
  }>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [currentPreviewFile, setCurrentPreviewFile] = useState<string | null>(
    null,
  );
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string | null>(null);
  const [previewSignedUrl, setPreviewSignedUrl] = useState<string | null>(null);

  const hasFile = (
    record: RequirementRecord | null,
    requirementId: number,
  ): boolean => {
    // Si el archivo fue eliminado localmente, no mostrar como si tuviera archivo
    if (editedValues[requirementId]?.fileDeleted) {
      return false;
    }
    return !!(record?.fileBucket && record?.fileKey);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const handleEditClick = (
    requirementId: number,
    record: RequirementRecord | null,
  ) => {
    setEditingId(requirementId);
    setEditedValues({
      [requirementId]: {
        completed: record?.completed ?? false,
        observation: record?.observation ?? '',
        fileDeleted: false,
      },
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedValues({});
  };

  const handleSaveEdit = async (requirementId: number) => {
    setLoading(true);
    try {
      const editValues = editedValues[requirementId];
      let idIsNumber = false;
      if (!Number.isNaN(Number(entityId))) {
        idIsNumber = true;
      }
      const data = new FormData();
      data.append('completed', editValues.completed.toString());
      data.append('observation', editValues.observation);
      data.append('fileDeleted', editValues.fileDeleted.toString());
      data.append('requirementTemplateId', requirementId.toString());
      data.append('entityKey', entityKey.toString());
      data.append('entityId', entityId.toString());
      data.append('idIsNumber', idIsNumber.toString());
      if (editValues.file) {
        data.append('file', editValues.file);
      }
      await registerRequirementRecord(dispatch, data);
      setEditingId(null);
      setEditedValues({});
      void fetchRequirements();
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al guardar los cambios');
      setLoading(false);
    }
  };

  const handleDeleteFile = (requirementId: number) => {
    // Marcar el archivo como eliminado localmente para mostrar la UI de subida
    setEditedValues((prev) => ({
      ...prev,
      [requirementId]: {
        ...prev[requirementId],
        fileDeleted: true,
      },
    }));
  };

  const getContentTypeFromFileName = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const contentTypes: { [key: string]: string } = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      bmp: 'image/bmp',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      txt: 'text/plain',
      md: 'text/markdown',
      json: 'application/json',
      xml: 'application/xml',
      html: 'text/html',
      css: 'text/css',
      js: 'text/javascript',
      ts: 'text/typescript',
      jsx: 'text/javascript',
      tsx: 'text/typescript',
      csv: 'text/csv',
      log: 'text/plain',
    };
    return contentTypes[extension || ''] || 'application/octet-stream';
  };

  const handlePreviewFile = async (record: RequirementRecord) => {
    try {
      setLoading(true);
      setPreviewLoading(true);
      setPreviewError(null);
      const fileName = record.fileKey || `requirement-${record.id}`;

      // Try to get the file as blob through backend (avoids CORS issues)
      const blobResponse = await getRequirementFileAsBlob(dispatch, record.id);

      if (blobResponse) {
        // Successfully got blob from backend
        const newBlobUrl = URL.createObjectURL(blobResponse.blob);
        setBlobUrl(newBlobUrl);
        setContentType(
          blobResponse.contentType || getContentTypeFromFileName(fileName),
        );
        setCurrentPreviewFile(fileName);

        // Also get signed URL for download
        const signedUrlResponse = await downloadRequirementFile(
          dispatch,
          record.id,
        );
        setPreviewSignedUrl(signedUrlResponse?.signedUrl || null);
        setPreviewOpen(true);
      } else {
        // Fallback: use signed URL directly (works for PDFs and images)
        const response = await downloadRequirementFile(dispatch, record.id);
        if (!response) {
          throw new Error('No se pudo obtener la URL del archivo');
        }

        const contentType = getContentTypeFromFileName(fileName);
        // Use the signed URL directly (avoids CORS issues for PDFs and images)
        setBlobUrl(response.signedUrl);
        setContentType(contentType);
        setCurrentPreviewFile(fileName);
        setPreviewSignedUrl(response.signedUrl);
        setPreviewOpen(true);
      }
    } catch (error) {
      console.error(error);
      setPreviewError('Error al cargar el archivo para previsualización');
      showAlert(dispatch, 'error', 'Error al cargar el archivo');
    } finally {
      setPreviewLoading(false);
      setLoading(false);
    }
  };

  const handleDownloadFile = async (record: RequirementRecord) => {
    try {
      setLoading(true);
      const response = await downloadRequirementFile(dispatch, record.id);
      if (!response) {
        throw new Error('No se pudo obtener la URL del archivo');
      }
      window.open(response.signedUrl, '_blank');
      showAlert(dispatch, 'success', 'Descarga completada');
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al descargar el archivo');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFromPreview = () => {
    if (previewSignedUrl) {
      window.open(previewSignedUrl, '_blank');
      showAlert(dispatch, 'success', 'Descarga iniciada');
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    // Only revoke blob URLs (not regular URLs)
    if (blobUrl && blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
    }
    setBlobUrl(null);
    setContentType(null);
    setCurrentPreviewFile(null);
    setPreviewSignedUrl(null);
    setPreviewError(null);
  };

  useEffect(() => {
    return () => {
      // Only revoke blob URLs (not regular URLs)
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const handleUploadFile = (
    requirementId: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setEditedValues((prev) => ({
        ...prev,
        [requirementId]: {
          ...prev[requirementId],
          file,
        },
      }));
    }
  };

  const handleCompletedChange = (requirementId: number, completed: boolean) => {
    setEditedValues((prev) => ({
      ...prev,
      [requirementId]: {
        ...prev[requirementId],
        completed,
      },
    }));
  };

  const handleObservationChange = (
    requirementId: number,
    observation: string,
  ) => {
    setEditedValues((prev) => ({
      ...prev,
      [requirementId]: {
        ...prev[requirementId],
        observation,
      },
    }));
  };

  const fetchRequirements = async () => {
    try {
      setLoading(true);
      let idIsNumber = false;
      if (!Number.isNaN(Number(entityId))) {
        idIsNumber = true;
      }
      const query = `category=${category}&idIsNumber=${idIsNumber}&entityKey=${entityKey}&entityId=${entityId}&`;
      const response = await getRequirements(dispatch, query);
      if (response) {
        setRequirements(response);
      }
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al obtener los requerimientos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchRequirements();
  }, []);

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>Requerimientos {entityAlias}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Header Row */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Requerimiento
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Estado
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Archivo
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Observación
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Fecha de Completado
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={1}></Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Data Rows */}
              {requirements.length === 0 ? (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No hay requerimientos disponibles
                    </Typography>
                  </Paper>
                </Grid>
              ) : (
                requirements.map((requirement) => {
                  const record = requirement.record;
                  const fileUploaded = hasFile(record, requirement.id);
                  const isEditing = editingId === requirement.id;
                  const editedValue = editedValues[requirement.id];
                  const displayCompleted = isEditing
                    ? (editedValue?.completed ?? record?.completed ?? false)
                    : (record?.completed ?? false);
                  const displayObservation = isEditing
                    ? (editedValue?.observation ?? record?.observation ?? '')
                    : (record?.observation ?? '');

                  return (
                    <Grid item xs={12} key={requirement.id}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          border: isEditing
                            ? '2px solid #1976d2'
                            : '1px solid #e0e0e0',
                          backgroundColor: isEditing ? '#f0f7ff' : '#fafafa',
                        }}
                      >
                        <Grid container spacing={1} alignItems="center">
                          {/* Requirement Name and Description */}
                          <Grid item xs={12} sm={3}>
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                sx={{ mb: 0.5 }}
                              >
                                {requirement.name}
                              </Typography>
                              {requirement.description && (
                                <Tooltip title={requirement.description}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {requirement.description}
                                  </Typography>
                                </Tooltip>
                              )}
                              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                                {requirement.required && (
                                  <Chip
                                    label="Requerido"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    sx={{ height: '18px', fontSize: '0.65rem' }}
                                  />
                                )}
                                {requirement.requiresFile && (
                                  <Chip
                                    label="Requiere archivo"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: '18px', fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Grid>

                          {/* Status */}
                          <Grid item xs={12} sm={2}>
                            {isEditing ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Switch
                                  checked={displayCompleted}
                                  onChange={(e) =>
                                    handleCompletedChange(
                                      requirement.id,
                                      e.target.checked,
                                    )
                                  }
                                  color="primary"
                                  size="small"
                                />
                                <Typography variant="body2">
                                  {displayCompleted
                                    ? 'Completado'
                                    : 'Pendiente'}
                                </Typography>
                              </Box>
                            ) : record ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {record.completed ? (
                                  <>
                                    <CheckCircleIcon
                                      sx={{
                                        color: 'success.main',
                                        fontSize: 20,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                    >
                                      Completado
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <CancelIcon
                                      sx={{
                                        color: 'warning.main',
                                        fontSize: 20,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="warning.main"
                                    >
                                      Pendiente
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Sin registro
                              </Typography>
                            )}
                          </Grid>

                          {/* File Status */}
                          <Grid item xs={12} sm={2}>
                            {isEditing ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                }}
                              >
                                {fileUploaded ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <AttachFileIcon
                                      sx={{
                                        color: 'success.main',
                                        fontSize: 20,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                      sx={{ flex: 1 }}
                                    >
                                      Archivo subido
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleDeleteFile(requirement.id)
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : editedValue?.file ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <AttachFileIcon
                                        sx={{
                                          color: 'primary.main',
                                          fontSize: 20,
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        color="primary.main"
                                        sx={{
                                          flex: 1,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {editedValue.file.name}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          setEditedValues((prev) => ({
                                            ...prev,
                                            [requirement.id]: {
                                              ...prev[requirement.id],
                                              file: undefined,
                                            },
                                          }));
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                    <input
                                      accept="*/*"
                                      style={{ display: 'none' }}
                                      id={`file-upload-${requirement.id}`}
                                      type="file"
                                      onChange={(e) =>
                                        handleUploadFile(requirement.id, e)
                                      }
                                    />
                                    <label
                                      htmlFor={`file-upload-${requirement.id}`}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="primary"
                                        sx={{
                                          cursor: 'pointer',
                                          textDecoration: 'underline',
                                        }}
                                      >
                                        Cambiar archivo
                                      </Typography>
                                    </label>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <input
                                      accept="*/*"
                                      style={{ display: 'none' }}
                                      id={`file-upload-${requirement.id}`}
                                      type="file"
                                      onChange={(e) =>
                                        handleUploadFile(requirement.id, e)
                                      }
                                    />
                                    <label
                                      htmlFor={`file-upload-${requirement.id}`}
                                    >
                                      <IconButton
                                        component="span"
                                        size="small"
                                        color="primary"
                                      >
                                        <CloudUploadIcon fontSize="small" />
                                      </IconButton>
                                    </label>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Subir archivo
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ) : fileUploaded ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <AttachFileIcon
                                  sx={{ color: 'success.main', fontSize: 20 }}
                                />
                                <Typography
                                  variant="body2"
                                  color="success.main"
                                >
                                  Archivo subido
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Sin archivo
                              </Typography>
                            )}
                          </Grid>

                          {/* Observation */}
                          <Grid item xs={12} sm={3}>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                                value={displayObservation}
                                onChange={(e) =>
                                  handleObservationChange(
                                    requirement.id,
                                    e.target.value,
                                  )
                                }
                                placeholder="Ingrese observación..."
                                variant="outlined"
                              />
                            ) : (
                              <Tooltip
                                title={record?.observation || 'Sin observación'}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {record?.observation || 'Sin observación'}
                                </Typography>
                              </Tooltip>
                            )}
                          </Grid>

                          {/* Completed Date */}
                          <Grid item xs={12} sm={1}>
                            {isEditing ? null : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {record?.completedAt
                                  ? formatDate(record.completedAt)
                                  : 'N/A'}
                              </Typography>
                            )}
                          </Grid>

                          {/* Actions */}
                          <Grid item xs={12} sm={1}>
                            {isEditing ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  justifyContent: 'flex-end',
                                }}
                              >
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    void handleSaveEdit(requirement.id);
                                  }}
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleCancelEdit}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  gap: 0.5,
                                }}
                              >
                                {fileUploaded && (
                                  <>
                                    <Tooltip title="Previsualizar archivo">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() =>
                                          void handlePreviewFile(record!)
                                        }
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Descargar archivo">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => {
                                          void handleDownloadFile(record!);
                                        }}
                                      >
                                        <DownloadIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleEditClick(requirement.id, record)
                                  }
                                  disabled={editingId !== null}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={onClose} disabled={loading}>
          Cerrar
        </CustomButton>
      </DialogActions>

      <DocumentsPreviewer
        open={previewOpen}
        onClose={handleClosePreview}
        fileName={''}
        blobUrl={blobUrl}
        contentType={contentType}
        loading={previewLoading}
        error={previewError}
        onDownload={handleDownloadFromPreview}
        onError={(error) => setPreviewError(error)}
      />
    </Dialog>
  );
};

export default RequirementsDialog;
